import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { TextField, Switch, FormControlLabel } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import {
  BooleanChoiceIcon,
  HeaderIcon,
  MultipleChoiceIcon,
  SingleChoiceIcon
} from '@splunk-iteng/splunk-shared-components'

const SplunkSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#CE0070',
    '&:hover': {
      backgroundColor: alpha('#CE0070', theme.palette.action.hoverOpacity)
    }
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#CE0070'
  }
}))

const muiFont =
  'MuiTypography-root MuiTypography-body1 MuiFormControlLabel-label css-rizt0-MuiTypography-root'

const typeEnum = {
  header: 'Header or Short Text',
  multipleChoice: 'Multiple Choice',
  enum: 'Dropdown Single Choice',
  singleChoice: 'Yes or No Question'
}

interface CardData {
  id: string
  app_name: string
  attribute_key: string
  attribute_value: string
  attribute_type: string
  attribute_order: number
  attribute_is_required: boolean
  attribute_is_published: boolean
}

export function CardZone({ appName, cards, onSave }: any) {
  const addCard = (cardType: string) => {
    const newValue = cardType === 'singleChoice' ? '["Yes", "No"]' : '[""]'
    const newID = uuidv4()
    const newCard: CardData = {
      id: newID,
      app_name: appName,
      attribute_key: '',
      attribute_value: newValue,
      attribute_type: cardType,
      attribute_order: cards.length,
      attribute_is_required: true,
      attribute_is_published: false
    }
    onSave([...cards, newCard])
  }
  const removeCard = (cardID: number) => {
    const filtered = cards.filter((card) => card.id !== cardID)
    const reordered = filtered.map((card, index) => {
      const newObj = { ...card, attribute_order: index }
      return newObj
    })
    onSave(reordered)
  }
  const addChoice = (cardID: number) => {
    const withNewChoice = cards.map((card) => {
      if (card.id === cardID) {
        const newValue = JSON.parse(card.attribute_value)
        newValue.push('')
        return { ...card, attribute_value: JSON.stringify(newValue) }
      }
      return card
    })
    onSave(withNewChoice)
  }
  const removeChoice = (cardID: number) => {
    const withNewChoice = cards.map((card) => {
      if (card.id === cardID) {
        const newValue = JSON.parse(card.attribute_value)
        if (newValue.length > 1) {
          newValue.pop()
          return { ...card, attribute_value: JSON.stringify(newValue) }
        }
        return card
      }
      return card
    })
    onSave(withNewChoice)
  }
  const updateKey = (cardID: number, keyValue: string) => {
    const withNewChoice = cards.map((card) => {
      if (card.id === cardID) {
        return { ...card, attribute_key: keyValue }
      }
      return card
    })
    onSave(withNewChoice)
  }
  const updateValue = (
    cardID: number,
    currentValueIndex: number,
    newValue: string
  ) => {
    const withNewChoice = cards.map((card) => {
      if (card.id === cardID) {
        const newValues = JSON.parse(card.attribute_value)
        newValues[currentValueIndex] = newValue
        return { ...card, attribute_value: JSON.stringify(newValues) }
      }
      return card
    })
    onSave(withNewChoice)
  }
  const updateIsRequired = (cardID: number) => {
    const withNewChoice = cards.map((card) => {
      if (card.id === cardID) {
        return { ...card, attribute_is_required: !card.attribute_is_required }
      }
      return card
    })
    onSave(withNewChoice)
  }
  const handleOrderChange = (currentIndex: number, newOrder: number) => {
    const targetIndex = newOrder - 1
    const updatedCards = [...cards]
    const temp = updatedCards[currentIndex]
    updatedCards[currentIndex] = updatedCards[targetIndex]
    updatedCards[targetIndex] = temp
    const reordered = updatedCards.map((card, index) => ({
      ...card,
      attribute_order: index
    }))
    onSave(reordered)
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {cards.map((card, cardIndex) => {
          const cardId = card.id
          const values: string[] = JSON.parse(card.attribute_value)
          return (
            <div
              key={`card-${cardId}`}
              style={{
                border: '1px solid #ccc',
                padding: '15px',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px'
                  }}
                >
                  <label htmlFor={`order-${cardId}`}>
                    <span className={muiFont}>Order:</span>
                  </label>
                  <select
                    id={`order-${cardId}`}
                    value={card.attribute_order + 1}
                    className={muiFont}
                    onChange={(e) =>
                      handleOrderChange(cardIndex, parseInt(e.target.value, 10))
                    }
                  >
                    {cards.map((_, i) => (
                      <option key={uuidv4()} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className={muiFont}>
                    {`Card Type: ${typeEnum[card.attribute_type]}`}
                  </span>
                </div>
              </div>
              <TextField
                id={`key-${cardId}`}
                label={
                  card.attribute_type === 'header'
                    ? 'Add a Header. Leave blank for short text only'
                    : 'Add your question here'
                }
                variant="outlined"
                size="medium"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateKey(cardId, event.target.value)
                }}
                onBlur={() => {
                  onSave(cards)
                }}
                value={card.attribute_key}
              />
              {values.map((currentValue, currentValueIndex) => (
                <TextField
                  id={`value-${cardId}-${currentValueIndex}`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`value-${cardId}-${currentValueIndex}`}
                  label={
                    card.attribute_type === 'header'
                      ? 'Add a short text. Leave blank for header text only'
                      : `Choice ${currentValueIndex}`
                  }
                  variant="outlined"
                  size="medium"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    updateValue(cardId, currentValueIndex, event.target.value)
                  }}
                  onBlur={() => {
                    onSave(cards)
                  }}
                  value={currentValue}
                />
              ))}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px'
                }}
              >
                <button
                  type="button"
                  className="attribute-remove-btn"
                  onClick={() => {
                    addChoice(card.id)
                  }}
                  onBlur={() => {
                    onSave(cards)
                  }}
                  style={{ marginRight: 10, flex: 'none', flexGrow: 0 }}
                  hidden={['header', 'singleChoice'].includes(
                    card.attribute_type
                  )}
                >
                  Add Choice
                </button>
                <button
                  type="button"
                  className="attribute-remove-btn"
                  onClick={() => {
                    removeChoice(card.id)
                  }}
                  style={{ marginRight: 10, flex: 'none', flexGrow: 0 }}
                  hidden={['header', 'singleChoice'].includes(
                    card.attribute_type
                  )}
                >
                  Remove Last Choice
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}
              >
                {card.attribute_type !== 'header' && (
                  <FormControlLabel
                    control={
                      <SplunkSwitch
                        checked={card.attribute_is_required}
                        onChange={() => {
                          updateIsRequired(card.id)
                        }}
                      />
                    }
                    label="Mark as required Field"
                    labelPlacement="end"
                  />
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px'
                  }}
                >
                  <button
                    type="button"
                    className="attribute-remove-btn"
                    onClick={() => {
                      removeCard(card.id)
                    }}
                    style={{ marginLeft: 10, flex: 'none', flexGrow: 0 }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="drag-drop-zone-buttons" style={{ marginTop: 10 }}>
        <button
          type="button"
          className="customize-btn customize-btn-inactive"
          onClick={() => addCard('header')}
          style={{ marginRight: 10 }}
        >
          <HeaderIcon />
          Add Title
        </button>
        <button
          type="button"
          className="customize-btn customize-btn-inactive"
          onClick={() => addCard('multipleChoice')}
          style={{ marginRight: 10 }}
        >
          <MultipleChoiceIcon />
          Multiple Choice
        </button>
        <button
          type="button"
          className="customize-btn customize-btn-inactive"
          onClick={() => addCard('enum')}
          style={{ marginRight: 10 }}
        >
          <SingleChoiceIcon />
          Dropdown-Single Select
        </button>
        <button
          type="button"
          className="customize-btn customize-btn-inactive"
          onClick={() => addCard('singleChoice')}
          style={{ marginRight: 10 }}
        >
          <BooleanChoiceIcon />
          Yes/No Binary Selection
        </button>
      </div>
    </div>
  )
}
